import { useCallback } from 'react';
import { useAppData } from '../contexts/AppDataContext';
import { ApiProblem } from '../../api/client';
import { deleteCase } from '../../api/cases';
import type { Case } from '../../domain/initial-cases';

// #691 — DELETING A REGISTRATION, which is not the same act as offboarding one.
//
// `lifecycle.ts` already has `requestDeletion` / `confirmDeletion`, and the
// names are close enough that the difference has to be stated rather than
// inferred. Those two move a case THROUGH its lifecycle: PENDING_DELETION, then
// CLOSED with `decision: 'DELETED'`. The row stays, the audit history stays, the
// registrant keeps a record that they were onboarded and then offboarded, and
// the case is still listable and still countable. That is the BCBP offboard.
//
// This is the administrative removal. `DELETE /api/v1/cases/:id` soft-deletes
// the row — `deleted_at` is set, every RLS-scoped read filters it out, and the
// registration stops existing as far as the portal is concerned. It is the
// action for a case that should never have been created: a duplicate, a test
// row, a submission promoted against the wrong company.
//
// They share one consequence and it is the expensive one. If the case reached
// the CTIA platform, the API enqueues the enterprise-delete on this path too
// (`settlementSyncStatus === 'SYNCED' || settlementSyncStep !== null`, the
// `app.delete` handler in apps/api/src/routes/cases.ts), so the branded
// enterprise is removed from BCID. That is why the dialog in front of this asks
// the operator to type the registrant's name, and why it says so out loud when
// the case is live at CTIA.
//
// ADMIN ONLY, AND THE SERVER IS THE GATE. The button is rendered behind
// `canAdmin`, but hiding a button closes nothing (the #543 lesson) — `deleteCase`
// is an exported function in api/cases.ts and this hook is on a context any page
// can reach. The enforcement is the role check on the DELETE route, which
// answers 403 to an analyst or an approver; this action's job on that 403 is to
// say so plainly rather than to pretend the click did nothing.
//
// IT RETURNS A BOOLEAN rather than throwing. Both call sites have follow-up work
// that is only correct on success — CaseDetail navigates back to the queue, the
// Customers Registrations tab refetches its submissions so the Case cell clears
// — and a caller that has to wrap this in its own try/catch to find that out is
// a caller that will eventually forget to.

export interface DeletionActions {
  /**
   * Soft-delete a registration. Resolves true when the server accepted it and
   * the case has been dropped from the store; false when it refused, in which
   * case the reason is already on screen as a toast.
   */
  deleteRegistration: (c: Case) => Promise<boolean>;
}

export function useDeletionActions(): DeletionActions {
  const { removeCase, showToast } = useAppData();

  const deleteRegistration = useCallback(async (c: Case): Promise<boolean> => {
    try {
      await deleteCase(c.id, c.version);
    } catch (err) {
      if (err instanceof ApiProblem && err.status === 403) {
        // The route's own wording, not a second opinion invented here. If the
        // server ever narrows who may delete, the operator reads the narrower
        // reason instead of a stale sentence compiled into the bundle.
        showToast(err.detail ?? err.title);
        return false;
      }
      if (err instanceof ApiProblem && err.status === 409) {
        // The If-Match carried this case's version. A 409 means somebody
        // changed the case between the page load and the click — including,
        // reachably, another admin deleting it first.
        showToast(`${c.id} changed since this page loaded it, so nothing was deleted.`
          + ' Reopen the registration and check it before trying again.');
        return false;
      }
      showToast(err instanceof ApiProblem
        ? (err.detail ? `${err.title}: ${err.detail}` : err.title)
        : (err instanceof Error ? err.message : String(err)));
      return false;
    }
    removeCase(c.id);
    showToast(`Registration ${c.id} (${c.registrant}) was deleted.`, 'success');
    return true;
  }, [removeCase, showToast]);

  return { deleteRegistration };
}
